import { useEffect, useState } from 'react';
import type { RenewalSummaryJobView } from '@responses/renewal-summary';

import { estimateProgress } from '../utils/progress';

const TICK_MS = 1_000;

/**
 * The time-based progress estimate for a job, re-evaluated every second.
 * Kept at its highest value per job, so the bar never moves backwards — e.g.
 * when the worker's `started_at` lands later than the `created_at` the
 * estimate was running from.
 */
export const useEstimatedProgress = (job: RenewalSummaryJobView | null | undefined): number => {
  const [progress, setProgress] = useState<{ jobId?: string; value: number }>({ value: 0 });
  const jobId = job?.id;
  const status = job?.status;
  const stage = job?.stage;
  const startedAt = job?.started_at ?? job?.created_at;

  useEffect(() => {
    if (!jobId || !status) return undefined;
    const update = () => {
      const estimate = estimateProgress({ status, stage, startedAt, now: Date.now() });
      setProgress((previous) => ({
        jobId,
        value: previous.jobId === jobId ? Math.max(previous.value, estimate) : estimate,
      }));
    };
    update();
    const timer = setInterval(update, TICK_MS);
    return () => clearInterval(timer);
  }, [jobId, stage, startedAt, status]);

  return progress.jobId === jobId ? progress.value : 0;
};
